import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getRedirectResult, signInWithPopup, signInWithRedirect, signOut } from "firebase/auth";
import { api } from "@/lib/api";
import { firebaseAuth, googleProvider } from "@/lib/firebase";

const POPUP_FALLBACK = ["auth/popup-blocked", "auth/operation-not-supported-in-this-environment"];
const CANCELLED = ["auth/popup-closed-by-user", "auth/cancelled-popup-request", "auth/user-cancelled"];

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const exchange = useCallback(async (fbUser) => {
    const idToken = await fbUser.getIdToken();
    const { data } = await api.post("/auth/firebase", { id_token: idToken });
    setUser(data);
  }, []);

  useEffect(() => {
    getRedirectResult(firebaseAuth)
      .then((r) => (r?.user ? exchange(r.user) : null))
      .catch(() => {})
      .finally(checkAuth);
  }, [checkAuth, exchange]);

  const login = async () => {
    try {
      const cred = await signInWithPopup(firebaseAuth, googleProvider);
      await exchange(cred.user);
    } catch (e) {
      if (POPUP_FALLBACK.includes(e?.code)) return signInWithRedirect(firebaseAuth, googleProvider);
      if (CANCELLED.includes(e?.code)) return;
      throw e;
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    await signOut(firebaseAuth).catch(() => {});
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout }}>{children}</AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
